import { useEffect, useState } from 'react';

/**
 * Shared "Store View" preview state. The sidebar persists the toggle in
 * localStorage under `sidebar_store_view` ('1' | '0') and broadcasts changes
 * via a `store-view-changed` CustomEvent (same-tab localStorage writes don't
 * fire the native `storage` event). Any component can read the current state
 * with this hook and stay in sync — e.g. to scope the Inventory Database to the
 * CCO Store while an admin is previewing the store sidebar.
 */
const STORAGE_KEY = 'sidebar_store_view';
export const STORE_VIEW_EVENT = 'store-view-changed';

function readStoreView(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function useStoreViewActive(): boolean {
  const [active, setActive] = useState<boolean>(readStoreView);

  useEffect(() => {
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setActive(typeof detail === 'boolean' ? detail : readStoreView());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setActive(readStoreView());
    };
    window.addEventListener(STORE_VIEW_EVENT, onCustom as EventListener);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(STORE_VIEW_EVENT, onCustom as EventListener);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return active;
}

export default useStoreViewActive;
